import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMarkNotificationRead, useRecentNotifications } from "../../api";
import type { AppNotification } from "../../types";
import { SkeletonBlock } from "../ui/AsyncState";

export function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const dropdownId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const notificationsQuery = useRecentNotifications(open);
  const markNotificationsAsRead = useMarkNotificationRead();
  const notifications = notificationsQuery.data ?? [];
  const unreadCount = notifications.filter(
    (notification) => !notification.readAt,
  ).length;
  const showUnreadDot = notificationsQuery.isSuccess ? unreadCount > 0 : true;

  useEffect(() => {
    if (open) {
      const toMarkRead = notifications
        .filter((notification) => !notification.readAt)
        .map((notification) => notification.id);
      if (toMarkRead.length > 0) {
        markNotificationsAsRead.mutate(toMarkRead);
      }
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [open]);

  return (
    <div className="notify-menu" ref={rootRef}>
      <button
        aria-controls={open ? dropdownId : undefined}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        className={`topbar-notify ${open ? "open" : ""}`.trim()}
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <path d="M10 2a6 6 0 00-6 6v3l-1.5 2.5h15L16 11V8a6 6 0 00-6-6z" />
          <path d="M8 16a2 2 0 004 0" strokeLinecap="round" />
        </svg>
        {showUnreadDot && <span className="notify-dot" />}
      </button>

      {open && (
        <div
          className="notifications-dropdown"
          id={dropdownId}
          role="dialog"
          aria-label="Recent notifications"
        >
          <div className="notifications-hdr">
            <div>
              <div className="notifications-title title-sm">Notifications</div>
              <div className="notifications-sub">
                {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
              </div>
            </div>
          </div>

          <div className="notifications-body">
            {notificationsQuery.isLoading && (
              <SkeletonBlock count={3} height={58} radius={6} />
            )}
            {notificationsQuery.isError && (
              <div className="notifications-empty">
                <strong className="notifications-empty-title title-medium title-subtle">Could not load notifications</strong>
                <span>Please try again shortly.</span>
              </div>
            )}
            {notificationsQuery.isSuccess && notifications.length === 0 && (
              <div className="notifications-empty">
                <strong className="notifications-empty-title title-medium title-subtle">No notifications yet</strong>
                <span>New tasks and account updates will appear here.</span>
              </div>
            )}
            {notificationsQuery.isSuccess && notifications.length > 0 && (
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onSelect={() => setOpen(false)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface NotificationRowProps {
  notification: AppNotification;
  onSelect: () => void;
}

function NotificationRow({ notification, onSelect }: NotificationRowProps) {
  const content = (
    <>
      <span
        className={`notification-kind notification-kind-${notification.kind}`}
        aria-hidden="true"
      />
      <span className="notification-copy">
        <span className="notification-title title-medium title-subtle">{notification.title}</span>
        <span className="notification-body">{notification.body}</span>
        <span className="notification-time">{notification.createdAt}</span>
      </span>
    </>
  );
  const className =
    `notification-item ${notification.readAt ? "" : "unread"}`.trim();

  if (notification.href) {
    return (
      <Link className={className} onClick={onSelect} to={notification.href}>
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
}
